import { useEffect } from 'react'
import { getAISummary, getRealtimeChatAISummary } from 'services/api/api.contact'
import ContactState from 'store/contact/contact.state'
import RootState from 'store/state'
import useInterval from './useInterval'
import useSmartAgentAPI from './useSmartAgentAPI'

interface UseGetAISummaryProps {
    contact: ContactState
    type: AISummaryType
}

type APISourceFn = typeof getRealtimeChatAISummary | typeof getAISummary
type SourceResponse = { data?: string; status: number } | undefined

export enum AISummaryType {
    REALTIME_CHAT = 'REALTIME_CHAT',
    VOICE_CHAT = 'VOICE_CHAT',
    PRE_CONTACT_EMAIL = 'PRE_CONTACT_EMAIL',
}

export enum AISummaryStatus {
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
    PENDING = 'PENDING',
}

interface AISummaryResponse {
    status: AISummaryStatus
    summaryText: string
}

export const useGetAISummary = ({ contact, type }: UseGetAISummaryProps) => {
    const sourceFn = getSourceFn(type)
    const isAPISource = isAPISourceFn(sourceFn, type)

    const [_error, loading, response, request] = useSmartAgentAPI<
        string,
        RootState,
        SourceResponse
    >(async (state) => {
        if (isAPISource) {
            return sourceFn(state.app.ID, state.app.instance!.ID, state.auth.token!, contact?.ID)
        }

        return sourceFn(contact)
    })

    useEffect(() => {
        request()
    }, [contact?.attributes?.['sa-email-pre-contact-summary-status'], contact?.ID])

    if (isAPISource) {
        useInterval(() => {
            if (response?.status !== 204) return
            request()
        }, 5000)
    }

    return createResponse(response, isAPISource ? loading : false)
}

function createResponse(
    response: { data?: string; status?: number } | undefined,
    loading?: boolean,
): AISummaryResponse {
    const status = getStatus(loading ? 204 : response?.status)
    return {
        status,
        summaryText: getSummaryText(response?.data, status),
    }
}

function getSummaryText(data: string | undefined, status: AISummaryStatus): string {
    if (status === AISummaryStatus.PENDING) {
        return 'AI summary is loading'
    }

    return data || 'AI summary is unavailable'
}

function getStatus(code = 0): AISummaryStatus {
    switch (code) {
        case 0:
        case 200:
            return AISummaryStatus.SUCCESS
        case 204:
            return AISummaryStatus.PENDING
        default:
            return AISummaryStatus.ERROR
    }
}

function getSourceFn(type: AISummaryType) {
    const sourceFnMap = {
        [AISummaryType.REALTIME_CHAT]: getRealtimeChatAISummary,
        [AISummaryType.VOICE_CHAT]: getAISummary,
        [AISummaryType.PRE_CONTACT_EMAIL]: getAISummaryFromContactAttributes,
    }

    return sourceFnMap[type]
}

function isAPISourceFn(_fn: unknown, type: AISummaryType): _fn is APISourceFn {
    return type !== AISummaryType.PRE_CONTACT_EMAIL
}

async function getAISummaryFromContactAttributes(contact?: ContactState): Promise<SourceResponse> {
    if (!contact) {
        return {
            status: 500,
        }
    }

    const { attributes } = contact

    const summary = attributes['sa-email-pre-contact-summary']

    const status = getPseudoHTTPStatusFromAttributes(
        attributes['sa-email-pre-contact-summary-status'] as AISummaryStatus,
    )

    return {
        data: summary,
        status,
    }
}

function getPseudoHTTPStatusFromAttributes(
    status: AISummaryStatus = AISummaryStatus.PENDING,
): number {
    switch (status) {
        case AISummaryStatus.SUCCESS:
            return 200
        case AISummaryStatus.PENDING:
            return 204
        case AISummaryStatus.ERROR:
            return 500
        default:
            return 500
    }
}
